import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Room, RoomEvent, Track, VideoPresets } from 'livekit-client';
import GlobalStateContext from './context';
import { _notification } from './utils';
import environments from './environments';
import { generateGuestName } from './utils/guestNameGenerator';

const useIsMounted = () => {
  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);
  return isMounted;
};

const useWebRTC = (props) => {
  const [state] = useContext(GlobalStateContext);
  const { io, streamMediaConfig } = state;
  const [roomid, setRoomId] = useState(props.roomid);
  const [requestAccepted, setCallAccepted] = useState(false);
  const [eventEnded, setCallEnded] = useState(false);
  const [localStreams, rtcSetLocalStreams] = useState([]);
  const [name, setName] = useState(props.name || generateGuestName());
  const [requests, setRequests] = useState([]);
  const myId = io ? io.id : '';
  const [waitingApproval, setWaitingApproval] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [streamPeerMap, setStreamPeerMap] = useState({});
  const [streamState, setStreamState] = useState({});
  const shadowGuy = '__rcdr__';

  const roomRef = useRef(null);
  const publisherRef = useRef(null);
  const tokenRef = useRef(null);
  const connectingRef = useRef(false);
  const pendingPublishRef = useRef(null);
  const isMounted = useIsMounted();

  const updateRemoteStreamList = useCallback((track, participant, isSubscribed) => {
    if (track.kind !== Track.Kind.Video && track.kind !== 'video') {
      return;
    }

    const streamId = track.sid || participant.identity;

    if (!isSubscribed) {
      setRemoteStreams(prev => {
        const next = { ...prev };
        delete next[streamId];
        return next;
      });
      return;
    }

    // Build standard MediaStream for VideoStreamItem / video element
    const mediaStream = new MediaStream([track.mediaStreamTrack]);

    // Attach participant audio track if already subscribed
    const audioPub = participant.getTrackPublication(Track.Source.Microphone);
    if (audioPub && audioPub.track && audioPub.track.mediaStreamTrack) {
      mediaStream.addTrack(audioPub.track.mediaStreamTrack);
    }

    const isScreen = track.source === Track.Source.ScreenShare;

    const streamWrapper = {
      id: streamId,
      streamId: streamId,
      participantIdentity: participant.identity,
      name: participant.name || 'Guest',
      mediaStream: mediaStream,
      getMediaStream: () => mediaStream,
      typeOfVideo: isScreen ? 'SCREEN' : 'CAMERA',
      videoActive: true,
      audioActive: true,
      connection: {
        data: JSON.stringify({
          name: participant.name,
          producer: { producerId: participant.identity }
        })
      }
    };

    setRemoteStreams(prev => ({
      ...prev,
      [streamId]: streamWrapper
    }));

    setStreamPeerMap(prev => ({
      ...prev,
      [streamId]: participant.identity
    }));
  }, []);

  // Publishes existing MediaStream tracks to an already-connected LiveKit room.
  // Called internally after room.connect() or externally via publishStreamToRoom().
  const publishLocalTracksToRoom = async (room, stream) => {
    try {
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      if (videoTrack && !room.localParticipant.getTrackPublication(Track.Source.Camera)) {
        await room.localParticipant.publishTrack(videoTrack, { source: Track.Source.Camera });
      }
      if (audioTrack && !room.localParticipant.getTrackPublication(Track.Source.Microphone)) {
        await room.localParticipant.publishTrack(audioTrack, { source: Track.Source.Microphone });
      }
      publisherRef.current = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track ?? null;
      console.log('[LiveKit] Local tracks published to room');
    } catch (err) {
      console.warn('[LiveKit] Failed to publish local tracks:', err.message);
    }
  };

  // Called by WebMediaStreamRefactored when the local MediaStream (from getUserMedia) is ready.
  // Updates the local stream list for the UI and publishes to the LiveKit room.
  const publishStreamToRoom = useCallback((stream) => {
    if (!stream) return;

    const localWrapper = {
      id: 'local',
      streamId: 'local',
      mediaStream: stream,
      getMediaStream: () => stream,
      typeOfVideo: 'CAMERA',
      isLocal: true,
      videoActive: true,
      audioActive: true,
      name,
      connection: {
        data: JSON.stringify({
          name,
          producer: { producerId: 'local', name }
        })
      }
    };
    rtcSetLocalStreams([localWrapper]);

    const room = roomRef.current;
    if (!room || room.state !== 'connected') {
      // Room not ready yet — store and publish once it connects
      pendingPublishRef.current = stream;
      console.log('[LiveKit] Room not connected yet, stream queued for publish');
      return;
    }

    publishLocalTracksToRoom(room, stream).catch(e =>
      console.warn('[LiveKit] publishStreamToRoom error:', e.message)
    );
  }, [name]);

  const connectToLiveKit = useCallback(async ({ token, host }) => {
    if (!token) return;

    if (connectingRef.current) {
      console.log('[LiveKit] Connection already in progress, skipping duplicate');
      return;
    }

    if (tokenRef.current === token && roomRef.current?.state === 'connected') {
      console.log('[LiveKit] Already connected to room with this token');
      return;
    }

    connectingRef.current = true;
    tokenRef.current = token;

    if (roomRef.current) {
      console.log('[LiveKit] Disconnecting previous room');
      try {
        await roomRef.current.disconnect();
      } catch (e) {
        // ignore
      }
    }

    try {
      const rawUrl = host || environments.app?.api?.streamingServer?.livekit || 'ws://localhost:7880';
      // Upgrade to secure WebSocket when on HTTPS; also strip :7880/:7881 (plain LiveKit
      // ports) since SSL termination is at the reverse proxy on 443.
      const livekitUrl = window.location.protocol === 'https:'
        ? rawUrl
            .replace(/^ws:\/\//, 'wss://')
            .replace(/^http:\/\//, 'wss://')
            .replace(/:7880$/, '')
            .replace(/:7881$/, '')
        : rawUrl;
      console.log(`[LiveKit] Connecting to ${livekitUrl} with token`);

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution
        }
      });

      roomRef.current = room;

      // Handle remote tracks subscribed
      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log('[LiveKit] TrackSubscribed:', track.kind, participant.identity);
        updateRemoteStreamList(track, participant, true);
      });

      // Handle remote tracks unsubscribed
      room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        console.log('[LiveKit] TrackUnsubscribed:', track.kind, participant.identity);
        updateRemoteStreamList(track, participant, false);
      });

      // Handle remote participant leaving
      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('[LiveKit] ParticipantDisconnected:', participant.identity);
        setRemoteStreams(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(key => {
            if (next[key]?.participantIdentity === participant.identity) {
              delete next[key];
            }
          });
          return next;
        });
      });

      // Handle room disconnect
      room.on(RoomEvent.Disconnected, () => {
        console.log('[LiveKit] Room disconnected');
        if (isMounted.current) {
          setRemoteStreams({});
        }
      });

      await room.connect(livekitUrl, token);
      console.log('[LiveKit] Successfully connected to room:', room.name);

      // If a local MediaStream was ready before the room connected, publish it now
      if (pendingPublishRef.current && name !== shadowGuy) {
        const pending = pendingPublishRef.current;
        pendingPublishRef.current = null;
        publishLocalTracksToRoom(room, pending).catch(e =>
          console.warn('[LiveKit] Could not publish pending tracks after connect:', e.message)
        );
      }
    } catch (err) {
      if (err?.message?.includes('Client initiated disconnect') || (err?.name === 'ConnectionError' && err?.message?.includes('cancelled'))) {
        console.log('[LiveKit] Connection canceled by client (switching/cleaning up)');
        return;
      }
      console.error('[LiveKit] Connection error:', err);
      _notification.error('Failed to connect to LiveKit video server.');
    } finally {
      connectingRef.current = false;
    }
  }, [name, shadowGuy, streamMediaConfig, updateRemoteStreamList, isMounted]);

  /**
   * Replace local video track (seamlessly called by MediaPipeline.js for blur/background)
   */
  const replacePublisherTrack = useCallback(async (newTrack) => {
    if (!roomRef.current || !roomRef.current.localParticipant) {
      console.warn('[LiveKit] Cannot replace track: no active local participant');
      return;
    }

    try {
      console.log('[LiveKit] Replacing camera video track with:', newTrack.id);
      const camPub = roomRef.current.localParticipant.getTrackPublication(Track.Source.Camera);
      if (camPub && camPub.track) {
        await camPub.track.replaceTrack(newTrack);
        console.log('[LiveKit] Track replacement successful');
      }
    } catch (err) {
      console.error('[LiveKit] Failed to replace camera track:', err);
    }
  }, []);

  /**
   * Screen sharing toggle
   */
  const shareScreen = useCallback(async (enable = true) => {
    if (!roomRef.current || !roomRef.current.localParticipant) return;
    try {
      await roomRef.current.localParticipant.setScreenShareEnabled(enable);
      if (props.screenShareCallback) {
        props.screenShareCallback(enable);
      }
    } catch (err) {
      console.error('[LiveKit] Error toggling screen share:', err);
      _notification.error('Screen sharing failed.');
    }
  }, [props]);

  /**
   * Enter studio (moderator flow)
   */
  const enterStudio = useCallback(() => {
    if (!io) return;
    io.emit('enterStudio', { roomid, id: myId || io.id, name });
  }, [io, roomid, myId, name]);

  /**
   * Request to join (guest flow)
   */
  const requestToJoin = useCallback((targetRoomId) => {
    if (!io) return;
    setWaitingApproval(true);
    io.emit('requestToJoin', { roomid: targetRoomId || roomid, from: myId || io.id, name });
  }, [io, roomid, myId, name]);

  /**
   * Approve guest request (moderator action)
   */
  const acceptRequest = useCallback(({ from, name: guestName }) => {
    if (!io) return;
    io.emit('acceptRequest', { from: myId || io.id, to: from, roomid, name: guestName });
    setRequests(prev => prev.filter(req => req.from !== from));
  }, [io, myId, roomid]);

  /**
   * Deny guest request (moderator action)
   */
  const denyRequest = useCallback(({ from }) => {
    if (!io) return;
    io.emit('denyRequest', { from: myId || io.id, to: from, roomid });
    setRequests(prev => prev.filter(req => req.from !== from));
  }, [io, myId, roomid]);

  /**
   * Leave studio
   */
  const leave = useCallback(() => {
    setCallEnded(true);
    if (roomRef.current) {
      roomRef.current.disconnect();
    }
    if (io) {
      io.emit('leaving', roomid);
    }
  }, [io, roomid]);

  const requestToDisable = useCallback((action) => {
    // Optional disable signals
  }, []);

  // Wire up Socket.IO event listeners
  useEffect(() => {
    if (!io) return;

    const handleJoined = ({ clients, names: partNames, streamState: stState, waitlist }) => {
      console.log('[LiveKit Hook] Joined studio:', { clients, partNames, waitlist });
      if (stState) setStreamState(stState);
      if (waitlist) setRequests(waitlist);
    };

    const handleIncomingRequest = (req) => {
      console.log('[LiveKit Hook] Incoming request to join:', req);
      setRequests(prev => [...prev.filter(r => r.from !== req.from), req]);
    };

    const handleRequestAccepted = ({ from, streamState: stState }) => {
      console.log('[LiveKit Hook] Request accepted by moderator');
      setWaitingApproval(false);
      setCallAccepted(true);
      if (stState) setStreamState(stState);
    };

    const handleRequestDenied = () => {
      setWaitingApproval(false);
      _notification.error('Your request to join the studio was declined.');
    };

    const handleToken = ({ token, host }) => {
      console.log('[LiveKit Hook] Token received');
      connectToLiveKit({ token, host });
    };

    const handleUpdateStageState = (stageData) => {
      setStreamState(stageData);
    };

    io.on('joined', handleJoined);
    io.on('incomingRequest', handleIncomingRequest);
    io.on('requestAccepted', handleRequestAccepted);
    io.on('requestDenied', handleRequestDenied);
    io.on('livekit_token', handleToken);
    io.on('updateStageState', handleUpdateStageState);

    // Enter studio if moderator, otherwise request to join
    if (props.isModerator) {
      enterStudio();
    } else {
      requestToJoin(roomid);
    }

    return () => {
      io.off('joined', handleJoined);
      io.off('incomingRequest', handleIncomingRequest);
      io.off('requestAccepted', handleRequestAccepted);
      io.off('requestDenied', handleRequestDenied);
      io.off('livekit_token', handleToken);
      io.off('updateStageState', handleUpdateStageState);
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, [io, roomid, props.isModerator, enterStudio, requestToJoin, connectToLiveKit]);

  return {
    requests,
    requestAccepted,
    localStreams,
    remoteStreams,
    rtcSetLocalStreams,
    name,
    setName,
    eventEnded,
    myId,
    requestToJoin,
    leave,
    acceptRequest,
    denyRequest,
    setRoomId,
    roomid,
    streamPeerMap,
    streamState,
    setStreamState,
    waitingApproval,
    requestToDisable,
    shareScreen,
    shadowGuy,
    replacePublisherTrack,
    publishStreamToRoom,
    publisher: publisherRef.current
  };
};

export { useWebRTC };
export default useWebRTC;
